/**
 * Utilitários para escala de gráficos e detecção de outliers
 */

/**
 * Detecta outliers usando o método IQR (Interquartile Range)
 * @param {number[]} values - Array de valores numéricos
 * @returns {{ outliers: number[], threshold: number, categories: string[] }} Valores outliers e threshold
 */
export const detectOutliers = (values, categories = []) => {
  if (!values || values.length < 4) {
    return { outliers: [], threshold: Infinity, outlierCategories: [] }
  }

  const sorted = [...values].filter(v => v > 0).sort((a, b) => a - b)
  if (sorted.length < 4) {
    return { outliers: [], threshold: Infinity, outlierCategories: [] }
  }

  const q1Index = Math.floor(sorted.length * 0.25)
  const q3Index = Math.floor(sorted.length * 0.75)
  const q1 = sorted[q1Index]
  const q3 = sorted[q3Index]
  const iqr = q3 - q1
  const threshold = q3 + 1.5 * iqr

  const outlierCategories = []
  const outliers = values.filter((v, i) => {
    if (v > threshold) {
      if (categories[i]) {
        outlierCategories.push(categories[i])
      }
      return true
    }
    return false
  })

  return { outliers, threshold, outlierCategories }
}

/**
 * Calcula o valor máximo da escala excluindo outliers
 * @param {number[]} values - Array de valores
 * @returns {number} Valor máximo para a escala
 */
export const getScaleMaxWithoutOutliers = (values) => {
  if (!values || values.length === 0) return 100

  const { threshold } = detectOutliers(values)
  const filteredValues = values.filter(v => v <= threshold && v > 0)

  if (filteredValues.length === 0) {
    return Math.max(...values) * 1.1
  }

  const maxFiltered = Math.max(...filteredValues)
  return maxFiltered * 1.1
}

/**
 * Transforma dados de categorias em porcentagens
 * @param {Object[]} chartData - Dados do gráfico com meses e categorias
 * @param {string[]} categoryNames - Nomes das categorias
 * @returns {Object[]} Dados transformados em porcentagem
 */
export const toPercentage = (chartData, categoryNames) => {
  return chartData.map(monthData => {
    const total = categoryNames.reduce((sum, cat) => {
      const value = parseFloat(monthData[cat]?.toString().replace(/[^\d.-]/g, '')) || 0
      return sum + value
    }, 0)

    if (total === 0) return { ...monthData }

    const percentageData = { month: monthData.month }
    categoryNames.forEach(cat => {
      const value = parseFloat(monthData[cat]?.toString().replace(/[^\d.-]/g, '')) || 0
      percentageData[cat] = (value / total) * 100
    })

    return percentageData
  })
}

/**
 * Extrai todos os valores de categorias dos dados do gráfico
 * @param {Object[]} chartData - Dados do gráfico
 * @param {string[]} categoryNames - Nomes das categorias
 * @returns {{ values: number[], categories: string[] }} Valores e categorias correspondentes
 */
export const extractCategoryValues = (chartData, categoryNames) => {
  const values = []
  const categories = []

  categoryNames.forEach(cat => {
    const categoryTotal = chartData.reduce((sum, monthData) => {
      return sum + (parseFloat(monthData[cat]?.toString().replace(/[^\d.-]/g, '')) || 0)
    }, 0)
    values.push(categoryTotal)
    categories.push(cat)
  })

  return { values, categories }
}

/**
 * Formata valor conforme modo de escala
 * @param {number} value - Valor a formatar
 * @param {string} scaleMode - Modo de escala ('absolute', 'percentage', 'log', 'normalized')
 * @param {string} currency - Código da moeda (default: 'BRL')
 * @returns {string} Valor formatado
 */
export const formatValueByMode = (value, scaleMode, currency = 'BRL') => {
  if (value === null || value === undefined || value === 0) return null

  if (scaleMode === 'percentage') {
    return `${value.toFixed(1)}%`
  }

  return value.toLocaleString('pt-BR', { style: 'currency', currency })
}

/**
 * Retorna configuração do eixo Y baseada no modo de escala
 * @param {string} scaleMode - Modo de escala
 * @param {number} maxValue - Valor máximo calculado (para modo normalized)
 * @returns {Object} Configuração do yAxis
 */
export const getYAxisConfig = (scaleMode, maxValue) => {
  switch (scaleMode) {
    case 'percentage':
      return { scaleType: 'linear', max: 100 }
    case 'normalized':
      return { scaleType: 'linear', max: maxValue }
    default:
      return { scaleType: 'linear' }
  }
}

/**
 * Constantes para os modos de escala
 */
export const SCALE_MODES = {
  ABSOLUTE: 'absolute',
  PERCENTAGE: 'percentage',
  NORMALIZED: 'normalized'
}

/**
 * Labels para os modos de escala
 */
export const SCALE_MODE_LABELS = {
  absolute: 'R$',
  percentage: '%',
  normalized: 'Auto'
}

/**
 * Descrições para tooltips dos modos
 */
export const SCALE_MODE_TOOLTIPS = {
  absolute: 'Valores absolutos em reais',
  percentage: 'Proporção de cada categoria (100% total)',
  normalized: 'Escala ajustada excluindo outliers'
}
